import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import ReportTemplate from '../../components/reports/ReportTemplate';
import CSVExportButton from '../../components/reports/CSVExportButton';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { toast } from 'react-toastify';
import { ROUTES } from '../../consts/general';
import { MISSING_INVENTORY_COST } from '../../consts/reports';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import FilterDropdown from '../../components/FilterDropdown';
import { ACTIVE, IN_ACTIVE, SUNSET, PHASED_STATUSES, PLANNED, enumInventoryPhasedStatus } from '../../consts/inventoryStatus';
import { YES, NO, BOOLEAN_FILTERS, enumCommonBooleanStatuses } from '../../consts/common';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { MISSING_INVENTORY_COST as MISSING_INVENTORY_COST_CRON_TYPE } from '../../consts/cronTypes';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';

const MissingInventoryCostReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
  const emailSettings = allSettings['missing-inventory-cost'] || {};
  const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
  const sendHistory = allSendHistory['missing-inventory-cost'] || [];
  const { beginRequest, isLatest } = useAbortableRequest();
  const initialReady = useInitialMountDelay(500);
  const [isOpenSettings, setIsOpenSettings] = useState(false);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const pageSize = 50;

  // Filters
  const [skuSearch, setSkuSearch] = useState('');
  const [debouncedSkuSearch, setDebouncedSkuSearch] = useState('');
  const [selectedPhasedStatuses, setSelectedPhasedStatuses] = useState([ACTIVE, SUNSET]);
  const [selectedKitStatuses, setSelectedKitStatuses] = useState([YES, NO]);
  const [selectedIsReplenishable, setSelectedIsReplenishable] = useState([YES, NO]);
  const [selectedAmazonStatuses, setSelectedAmazonStatuses] = useState([YES, NO]);

  const columns = useMemo(
    () => [
      { key: 'sku', label: 'SKU', type: 'string', sortable: true, sticky: true },
      { key: 'landed_cost', label: 'Landed Cost', type: 'currency', sortable: true, align: 'right' },
      { key: 'weighted_cost', label: 'Weighted Cost', type: 'currency', sortable: true, align: 'right' },
    ],
    []
  );

  // Debounce SKU search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSkuSearch(skuSearch.trim()), 300);
    return () => clearTimeout(t);
  }, [skuSearch]);

  const fetchData = useCallback(async () => {
    const { signal, requestId } = beginRequest();
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(pageSize),
      });
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDirection', sortDirection);
      }
      if (debouncedSkuSearch) {
        params.append('sku', debouncedSkuSearch);
      }
      if (selectedPhasedStatuses && selectedPhasedStatuses.length > 0) {
        params.append('phasedStatuses', selectedPhasedStatuses.join(','));
      }
      if (selectedKitStatuses && selectedKitStatuses.length > 0) {
        params.append('kit', selectedKitStatuses.join(','));
      }
      if (selectedIsReplenishable && selectedIsReplenishable.length > 0) {
        params.append('isReplenishable', selectedIsReplenishable.join(','));
      }
      if (selectedAmazonStatuses && selectedAmazonStatuses.length > 0) {
        params.append('amazon', selectedAmazonStatuses.join(','));
      }

      const res = await fetch(`/api/v1/reports/${MISSING_INVENTORY_COST}?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to fetch report data');
      }
      const json = await res.json();
      if (!isLatest(requestId)) return;
      setData(json.data || []);
      setTotalPages(json.totalPages || 1);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error(e);
      if (!isLatest(requestId)) return;
      setError(e.message);
      setData([]);
      setTotalPages(1);
    } finally {
      if (isLatest(requestId)) setIsLoading(false);
    }
  }, [currentPage, pageSize, sortField, sortDirection, debouncedSkuSearch, selectedPhasedStatuses, selectedKitStatuses, selectedIsReplenishable, selectedAmazonStatuses, beginRequest, isLatest]);

  useEffect(() => {
    if (!initialReady) return;
    fetchData();
  }, [initialReady, fetchData]);

  useEffect(() => {
    dispatch(getReportEmailSettings('missing-inventory-cost'));
    dispatch(getReportSendHistory('missing-inventory-cost'));
  }, [dispatch]);

  const handleSort = ({ field, direction }) => {
    setSortField(field);
    setSortDirection(direction);
    setCurrentPage(1);
  };

  const handleBackToReports = () => navigate(ROUTES.REPORTS);

  const handleSuccessSubmit = async (formData) => {
    const payload = {
      emailRecipients: formData?.emailRecipients || [],
      scheduleType: formData?.schedule || null,
      dayOfMonth: formData?.dayOfMonth ?? null,
      slackChannelId: formData?.slackChannelId ?? null,
      dayOfWeek: formData?.dayOfWeek ?? null,
      timeOfDay: formData?.timeOfDay ?? null,
      emailMessage: formData?.emailMessage ?? null,
      emailSendPreviewTo: formData?.emailSendPreviewTo ?? null,
    };

    try {
      await dispatch(updateReportEmailSettings({ reportType: 'missing-inventory-cost', ...payload })).unwrap();
      const cronInterval = buildCronInterval({
        scheduleType: payload.scheduleType,
        dayOfMonth: payload.dayOfMonth,
        dayOfWeek: payload.dayOfWeek,
        timeOfDay: payload.timeOfDay,
      });
      if (!cronInterval) {
        toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
        return;
      }
      const cronData = {
        id: emailSettings?.cronId,
        type: emailSettings?.cronType || MISSING_INVENTORY_COST_CRON_TYPE,
        cronInterval,
      };
      await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
      toast.success('Settings saved successfully', { position: 'bottom-right' });
      setIsOpenSettings(false);
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
    }
  };

  const sendPreviewEmail = async (formData) => {
    try {
      const payload = {
        email: formData?.emailSendPreviewTo,
        emailMessage: formData?.emailMessage || '',
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        sku: debouncedSkuSearch || null,
        phasedStatuses: selectedPhasedStatuses && selectedPhasedStatuses.length > 0 ? selectedPhasedStatuses : [ACTIVE, SUNSET],
        kit: selectedKitStatuses && selectedKitStatuses.length > 0 ? selectedKitStatuses : [],
        isReplenishable: selectedIsReplenishable && selectedIsReplenishable.length > 0 ? selectedIsReplenishable : [],
        amazon: selectedAmazonStatuses && selectedAmazonStatuses.length > 0 ? selectedAmazonStatuses : [],
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'missing-inventory-cost', ...payload })).unwrap();
      await dispatch(getReportSendHistory('missing-inventory-cost')).unwrap();
      toast.success('Preview email sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
    }
  };

  const sendPreviewSlack = async (formData) => {
    try {
      const payload = {
        slackChannelId: formData?.slackChannelId,
        emailMessage: formData?.emailMessage || '',
        sortField: sortField || null,
        sortDirection: sortDirection || null,
        sku: debouncedSkuSearch || null,
        phasedStatuses: selectedPhasedStatuses && selectedPhasedStatuses.length > 0 ? selectedPhasedStatuses : [ACTIVE, SUNSET],
        kit: selectedKitStatuses && selectedKitStatuses.length > 0 ? selectedKitStatuses : [],
        isReplenishable: selectedIsReplenishable && selectedIsReplenishable.length > 0 ? selectedIsReplenishable : [],
        amazon: selectedAmazonStatuses && selectedAmazonStatuses.length > 0 ? selectedAmazonStatuses : [],
      };
      await dispatch(sendReportPreviewEmail({ reportType: 'missing-inventory-cost', ...payload })).unwrap();
      await dispatch(getReportSendHistory('missing-inventory-cost')).unwrap();
      toast.success('Slack preview sent', { position: 'bottom-right' });
    } catch (e) {
      toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
    }
  };

  const renderPhasedStatusOption = (phase) => (
    <span className="text-gray-900">{enumInventoryPhasedStatus[phase]}</span>
  );

  const renderIsReplenishableOption = (status) => (
    <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
  );

  const renderAmazonStatusOption = (status) => (
    <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
  );

  const renderKitStatusOption = (status) => (
    <span className="text-gray-900">{enumCommonBooleanStatuses[status]}</span>
  );

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '$0.00';
    const num = Number(value);
    if (Number.isNaN(num)) return '$0.00';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  };

  const renderRows = () => {
    const rows = (data || []).map((row, idx) => (
      <tr key={`${row.sku || 'sku'}-${idx}`} className="bg-white">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
            {row.sku ? (
                <a
                    href={`${ROUTES.CATALOG}?search=${encodeURIComponent(row.sku)}&phasedStatuses=`}
                    className="text-blue-600 hover:underline"
                    target="_blank"
                    rel="noreferrer"
                >
                    {row.sku}
                </a>
            ) : (
                ''
            )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {formatCurrency(row.landed_cost)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
          {formatCurrency(row.weighted_cost)}
        </td>
      </tr>
    ));

    return rows;
  };

  const filtersComponent = (
    <div className="contents">
      <div className="flex flex-row items-center gap-2">
        <input
          type="text"
          value={skuSearch}
          onChange={(e) => {
            setSkuSearch(e.target.value);
            setCurrentPage(1);
          }}
          placeholder="Search SKU"
          className="w-40 sm:w-[160px] rounded-md border border-gray-300 px-3 py-2.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
        />
      </div>

      <div className="relative">
        <FilterDropdown
          options={PHASED_STATUSES.filter((status) => status !== IN_ACTIVE && status !== PLANNED)}
          selectedOptions={selectedPhasedStatuses}
          onChange={(value) => {
            setSelectedPhasedStatuses(value);
            setCurrentPage(1);
          }}
          placeholder="Product Status"
          renderOption={renderPhasedStatusOption}
          disableScrollRecalculation={true}
        />
      </div>

      <div className="relative">
        <FilterDropdown
          options={BOOLEAN_FILTERS}
          selectedOptions={selectedAmazonStatuses}
          onChange={(value) => {
            setSelectedAmazonStatuses(value);
            setCurrentPage(1);
          }}
          placeholder="Is Amazon"
          renderOption={renderAmazonStatusOption}
          disableScrollRecalculation={true}
        />
      </div>

      <div className="relative">
        <FilterDropdown
          options={BOOLEAN_FILTERS}
          selectedOptions={selectedKitStatuses}
          onChange={(value) => {
            setSelectedKitStatuses(value);
            setCurrentPage(1);
          }}
          placeholder="Is Kit"
          renderOption={renderKitStatusOption}
          disableScrollRecalculation={true}
        />
      </div>

      <div className="relative">
        <FilterDropdown
          options={BOOLEAN_FILTERS}
          selectedOptions={selectedIsReplenishable}
          onChange={(value) => {
            setSelectedIsReplenishable(value);
            setCurrentPage(1);
          }}
          placeholder="Is Replenishable"
          renderOption={renderIsReplenishableOption}
          disableScrollRecalculation={true}
        />
      </div>
      <Cog8ToothIcon className="h-5 w-5 text-primary cursor-pointer" onClick={() => setIsOpenSettings(true)} />
    </div>
  );

  const exportButton = (
    <CSVExportButton
      data={data}
      columns={[
        { key: 'sku', label: 'SKU' },
        { key: 'landed_cost', label: 'Landed Cost' },
        { key: 'weighted_cost', label: 'Weighted Cost' },
      ]}
      filename={`${MISSING_INVENTORY_COST}`}
      onExport={async () => {
        const params = new URLSearchParams({});
        if (sortField) {
          params.append('sortField', sortField);
          params.append('sortDirection', sortDirection);
        }
        if (debouncedSkuSearch) {
          params.append('sku', debouncedSkuSearch);
        }
        if (selectedPhasedStatuses && selectedPhasedStatuses.length > 0) {
          params.append('phasedStatuses', selectedPhasedStatuses.join(','));
        }
        if (selectedKitStatuses && selectedKitStatuses.length > 0) {
          params.append('kit', selectedKitStatuses.join(','));
        }
        if (selectedIsReplenishable && selectedIsReplenishable.length > 0) {
          params.append('isReplenishable', selectedIsReplenishable.join(','));
        }
        if (selectedAmazonStatuses && selectedAmazonStatuses.length > 0) {
          params.append('amazon', selectedAmazonStatuses.join(','));
        }
        const res = await fetch(`/api/v1/reports/${MISSING_INVENTORY_COST}/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Export failed');
        }
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        const contentDisposition = res.headers.get('content-disposition');
        const exportFilename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `${MISSING_INVENTORY_COST}_${new Date().toISOString().slice(0, 10)}.csv`;
        link.href = url;
        link.download = exportFilename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        return { success: true, handled: true };
      }}
    />
  );

  const paginationComponent =
    totalPages > 1 ? (
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <button
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      </div>
    ) : null;

  const breadcrumbs = [
    { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
    { label: '' },
  ];

  return (
    <>
      <ReportTemplate
        reportId={MISSING_INVENTORY_COST}
        description="Report showing missing Landed Cost and Weighted Cost for all products that are in orders"
        breadcrumbs={breadcrumbs}
        onBack={handleBackToReports}
        marginFromSidebar={marginFromSidebar}
        isLoading={isLoading}
        error={error}
        filters={filtersComponent}
        exportButton={exportButton}
        data={data}
        columns={columns}
        pagination={paginationComponent}
        emptyMessage="No products found."
        onSort={handleSort}
        sortField={sortField}
        cronType={emailSettings?.cronType || null}
        sortDirection={sortDirection}
        enableSorting={true}
      >
        {renderRows()}
      </ReportTemplate>

      <ReportEmailSettingsSidebar
        reportType={'missing-inventory-cost'}
        isOpen={isOpenSettings}
        marginFromSidebar={marginFromSidebar}
        onClose={() => setIsOpenSettings(false)}
        isLoading={isLoadingEmailSettings}
        onSuccessSubmit={handleSuccessSubmit}
        sendPreviewEmail={sendPreviewEmail}
        sendPreviewSlack={sendPreviewSlack}
        defaultSettings={emailSettings || {}}
        sendHistory={sendHistory}
        isLoadingSendHistory={isLoadingSendHistory}
        downloadFilenamePrefix="missing-inventory-cost"
      />
    </>
  );
};

export default MissingInventoryCostReport;
